// Angular modules
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpErrorResponse } from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import { HttpParameterCodec } from '@angular/common/http';
import { Inject } from '@angular/core';
import { Injectable } from '@angular/core';

// External modules
import { ArrayTyper } from '@caliatys/array-typer';
import { Observable } from 'rxjs';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { map } from 'rxjs/operators';
import { tap } from 'rxjs/operators';

// Services
import { AuthService } from './auth.service';

// Models
import { Message, SendingStatus } from '../models/message.model';
import { Account } from '../models/account.model';
import { Connector } from '../models/connector.model';
import { DeviceConfiguration, StorageConfiguration } from '../models/configuration.model';
import { Device } from '../models/device.model';
import { Filter } from '../models/page.model';
import { Page } from '../models/page.model';
import { AuditTrail } from '../models/audittrail.model';
import { Product } from '../models/product.model';

@Injectable()
export class ApiService {

  private static handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was:`, error.error);
    }
    return throwError(
      'Something bad happened; please try again later.');
  }

  constructor(@Inject('environment') private environment, private http: HttpClient, private authService: AuthService) {
  }

  getConnectors(): Observable<Array<Connector>> {
    return this.http.get<Array<Connector>>(
      `${this.environment.apiUrl}/api/v1/connectors`)
      .pipe(catchError(ApiService.handleError));
  }

  initializeDevice(connectorId: string, endPoint: string): Observable<boolean> {
    let params = new HttpParams({ encoder: new HttpUrlEncodingCodec() });
    params = params.append('connector', connectorId);
    params = params.append('endPoint', endPoint);
    return this.http.get<boolean>(
      `${this.environment.apiUrl}/api/v1/devices/initialize`,
      {
        params
      })
      .pipe(
        catchError(ApiService.handleError)
      );
  }

  copyConfiguration(deviceId: string, connectorId: string, endPoint: string): Observable<boolean> {
    let params = new HttpParams({ encoder: new HttpUrlEncodingCodec() });
    params = params.append('connector', connectorId);
    params = params.append('endPoint', endPoint);
    return this.http.get<boolean>(
      `${this.environment.apiUrl}/api/v1/devices/${deviceId}/configuration/copy`,
      {
        params
      })
      .pipe(
        catchError(ApiService.handleError)
      );
  }

  reemitConfiguration(deviceId: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.environment.apiUrl}/api/v1/devices/${deviceId}/configuration/reemit`)
      .pipe(
        catchError(ApiService.handleError)
      );
  }

  getDevices(limit: number, page: number, filters: Filter[], statusFilter: string[]): Observable<Page<Device>> {
    let params = new HttpParams({ encoder: new HttpUrlEncodingCodec() });
    filters.forEach(element => {
      params = params.append(element.criteriaKey, element.value);
    });
    params = params.set('limit', limit.toString());
    params = params.set('page', page.toString());
    params = params.append('deviceTypeFamily', 'FEWIS');
    if (statusFilter !== null) {
      statusFilter.forEach(st => {
        params = params.append('deviceStatus', st);
      });
    }
    
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/devices`,
      {
        params
      })
      .pipe(
        map(res => new Page(Device, res)),
        catchError(ApiService.handleError)
      );
  }

  comstatus(ids: string[]): Observable<{ internalId: string, status: SendingStatus, lastCallDate: string, lastReadingRequestDate: string, lastConfigurationSendingDate: string, configurationVersion: Number }[]> {
    return this.http.post<{ internalId: string, status: number, lastCallDate: string, lastReadingRequestDate: string, lastConfigurationSendingDate: string,  configurationVersion: Number}[]>(
      `${this.environment.apiUrl}/api/v1/devices/comstatus`,
      ids)
      .pipe(
        map(res => res.map(elm => ({
          internalId: elm.internalId,
          lastCallDate: elm.lastCallDate,
          lastReadingRequestDate: elm.lastReadingRequestDate,
          lastConfigurationSendingDate: elm.lastConfigurationSendingDate,
          configurationVersion: elm.configurationVersion,
          status: elm?.status
        })),
          catchError(ApiService.handleError),
        )
      );
  }

  sendCommand(id: string, operation: string, message: string): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.environment.apiUrl}/api/v1/devices/${id}/command`,
      message ? { request: operation, params: message } : { request: operation })
      .pipe(catchError(ApiService.handleError));
  }

  getMessages(id: string, from: Date, to: Date): Observable<Message[]> {
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/devices/${id}/messages`, { params: { fromDate: from.toISOString(), toDate: to.toISOString() } })
      .pipe(
        map(res => ArrayTyper.asArray(Message, res as any)),
        catchError(ApiService.handleError),
      );
  }

  exportMessages(id: string, from: Date, to: Date): Observable<Blob> {
    const headers = new HttpHeaders({ 'Accept': 'application/octet-stream' });
    return this.http.get(`${this.environment.apiUrl}/api/v1/devices/${id}/messages/export`, { params: { fromDate: from.toISOString(), toDate: to.toISOString() }, headers, responseType: 'blob' });
  }

  sendRefreshRequest(id: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.environment.apiUrl}/api/v1/devices/${id}/refresh`)
      .pipe(
        catchError(ApiService.handleError),
      );
  }

  sendAllRefreshRequest(page: number, pageSize = 10): Observable<any> {
    return this.http.get<any>(
      `${this.environment.apiUrl}/api/v1/devices/refresh`, { params: { limit: pageSize.toString(), page: page.toString(), deviceTypeFamily: 'FEWIS', deviceStatus: 'running' } })
      .pipe(
        catchError(ApiService.handleError),
      );
  }

  getDevice(id: string): Observable<Device> {
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/devices/${id}`)
      .pipe(
        map(res => new Device(res)),
        catchError(ApiService.handleError),
      );
  }

  getAuditTail(id: string, from: Date, to: Date): Observable<AuditTrail[]> {
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/devices/${id}/audittrail`, { params: { fromDate: from.toISOString(), toDate: to.toISOString() } })
      .pipe(
        map(res => ArrayTyper.asArray(AuditTrail, res as any)),
        catchError(ApiService.handleError),
      );
  }

  exportAudit(id: string, from: Date, to: Date): Observable<Blob> {
    const headers = new HttpHeaders({ 'Accept': 'application/octet-stream' });
    return this.http.get(`${this.environment.apiUrl}/api/v1/devices/${id}/audittrail/export`, { params: { fromDate: from.toISOString(), toDate: to.toISOString() }, headers, responseType: 'blob' });
  }

  sendConfigurationRequest(id: string): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.environment.apiUrl}/api/v1/devices/${id}/requestconfig`)
      .pipe(
        catchError(ApiService.handleError),
      );
  }

  getConfiguration(id: string): Observable<DeviceConfiguration> {
    return this.http.get(
      `${this.environment.apiUrl}/api/v1/devices/${id}/configuration`)
      .pipe(
        map(res => res ? new DeviceConfiguration(res) : null),
        catchError(ApiService.handleError),
      );
  }

  updateScheduling(id: string, configurationVersion: Number, scheduling: string): Observable<boolean> {
    return this.http.post<any>(
      `${this.environment.apiUrl}/api/v1/devices/${id}/callscheduling`, {
      'value': scheduling,
      'configurationVersion': configurationVersion
    })
      .pipe(catchError(ApiService.handleError));
  }

  updateConfiguration(id: string, configurationVersion: Number, storage: StorageConfiguration): Observable<boolean> {
    const storagesData = {};
    storagesData[storage.inputName] = storage;

    var parameters = null;
    if (configurationVersion === null) {
      parameters = { 'storages': storagesData }
    } else {
      parameters = { 
        'configurationVersion': configurationVersion,
        'storages': storagesData 
      }
    }

    return this.http.post<any>(
      `${this.environment.apiUrl}/api/v1/devices/${id}/configuration`, parameters)
      .pipe(catchError(ApiService.handleError));
  }

  account(): Observable<Account> {
    return this.http.get(`${this.environment.apiUrl}/api/v1/account`)
      .pipe(
        map(res => new Account(res)),
        tap(account => this.authService.accountChanged.emit(account)),
        catchError(ApiService.handleError),
      );
  }

  getProducts(): Observable<Product[]> {
    return this.http.get<Product[]>(`${this.environment.apiUrl}/api/v1/productdefinitions`)
      .pipe(
        catchError(ApiService.handleError),
      );
  }
}

export class HttpUrlEncodingCodec implements HttpParameterCodec {
  encodeKey(k: string): string { return standardEncoding(k); }
  encodeValue(v: string): string { return standardEncoding(v); }
  decodeKey(k: string): string { return decodeURIComponent(k); }
  decodeValue(v: string) { return decodeURIComponent(v); }
}
function standardEncoding(v: string): string {
  return encodeURIComponent(v);
}
